import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { CheckCircle, ShoppingCart, ArrowRight, Home, Shield, Zap, Star } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ThankYou = () => {
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);

  const fetchProduct = async () => {
    try {
      const response = await axios.get(`${API}/product`);
      setProduct(response.data);
    } catch (error) {
      console.error("Error fetching product:", error);
    }
  };

  useEffect(() => {
    fetchProduct();
  }, []);

  if (!product) return null;

  return (
    <div className="min-h-screen animated-gradient relative overflow-hidden">
      {/* Background effects */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-96 h-96 bg-teal-500/10 rounded-full blur-3xl"></div>
        <div className="absolute top-1/2 -left-40 w-[500px] h-[500px] bg-cyan-500/10 rounded-full blur-3xl"></div>
        <div className="absolute -bottom-40 right-1/3 w-96 h-96 bg-sky-500/10 rounded-full blur-3xl"></div>
      </div>

      <div className="relative z-10">
        {/* Header */}
        <header className="py-6 px-4 sm:px-6 lg:px-8 border-b border-teal-500/20 backdrop-blur-sm">
          <div className="max-w-7xl mx-auto flex items-center justify-between">
            <a 
              href="https://alcodot.com.tr" 
              className="logo-container cursor-pointer"
              aria-label="Ana Sayfaya Dön"
            >
              <div className="logo-ring"></div>
              <div className="logo-ring"></div>
              <img 
                src="/images/alcodot-logo.png" 
                alt="Alcodot Logo" 
                className="logo-image h-12 w-auto"
              />
            </a>
          </div>
        </header>

        {/* Main Content */}
        <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          {/* Success Message */}
          <div className="text-center mb-12 fade-in">
            <div className="inline-flex items-center justify-center w-24 h-24 bg-gradient-to-br from-green-500 to-emerald-600 rounded-full mb-6 shadow-2xl neon-glow">
              <CheckCircle className="w-12 h-12 text-white" />
            </div>
            <h1 className="text-5xl font-black text-gradient-glow mb-4">Teşekkürler!</h1>
            <p className="text-xl text-gray-300">Bilgileriniz başarıyla kaydedildi.</p>
          </div>

          {/* Product Offer */}
          {product.is_active && (
            <div className="glass-card-premium rounded-3xl p-10 neon-border mb-8 slide-up">
              <div className="text-center mb-8">
                <h2 className="text-3xl font-black text-white mb-3">Her Zaman Güvende Olun!</h2>
                <p className="text-lg text-gray-300">
                  Teorik hesaplama yerine <span className="text-teal-400 font-bold">profesyonel Alcodot test çubuğu</span> ile
                  <br />anında, kesin ve güvenilir sonuç alın.
                </p>
              </div>

              {/* Product showcase */}
              <div className="bg-gray-900/50 rounded-2xl p-4 sm:p-8 mb-6 sm:mb-8 border-2 border-teal-500/20">
                <div className="flex flex-col sm:flex-row items-center gap-6 sm:gap-8">
                  <div className="flex-shrink-0">
                    {product.image_url ? (
                      <div className="w-24 h-24 sm:w-32 sm:h-32 rounded-2xl sm:rounded-3xl overflow-hidden shadow-2xl neon-glow pulse-glow">
                        <img 
                          src={product.image_url} 
                          alt="Alcodot" 
                          className="w-full h-full object-cover"
                        />
                      </div>
                    ) : (
                      <div className="w-24 h-24 sm:w-32 sm:h-32 bg-gradient-to-br from-teal-500 to-cyan-600 rounded-2xl sm:rounded-3xl flex items-center justify-center shadow-2xl neon-glow pulse-glow">
                        <Shield className="w-12 h-12 sm:w-16 sm:h-16 text-white" />
                      </div>
                    )}
                  </div>

                  <div className="flex-1 w-full text-center sm:text-left">
                    <h3 className="text-xl sm:text-2xl font-black text-white mb-3 sm:mb-4">Alcodot Test Çubuğu</h3>
                    
                    {/* Features */}
                    <div className="space-y-2 sm:space-y-3 mb-4 sm:mb-6">
                      {product.features.map((feature, index) => (
                        <div key={index} className="flex items-center gap-2 sm:gap-3 text-gray-300 justify-center sm:justify-start">
                          <div className="w-5 h-5 sm:w-6 sm:h-6 bg-teal-500/20 rounded-full flex items-center justify-center flex-shrink-0">
                            <CheckCircle className="w-3 h-3 sm:w-4 sm:h-4 text-teal-400" />
                          </div>
                          <span className="text-xs sm:text-sm">{feature}</span>
                        </div>
                      ))}
                    </div>

                    {/* Price */}
                    <div className="flex flex-col sm:flex-row items-center sm:items-start gap-3 sm:gap-4">
                      <div className="text-center sm:text-left">
                        <p className="text-xs text-gray-400">Özel Fiyat</p>
                        <p className="text-3xl sm:text-4xl font-black text-gradient-glow">{product.price} TL</p>
                      </div>
                      {product.discount_text && (
                        <div className="bg-red-500/20 border-2 border-red-500/40 rounded-xl px-3 sm:px-4 py-2">
                          <p className="text-red-400 font-bold text-xs sm:text-sm">{product.discount_text}</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Social proof */}
              <div className="grid grid-cols-3 gap-3 sm:gap-4 mb-6 sm:mb-8">
                <div className="text-center p-3 sm:p-4 bg-gray-900/30 rounded-xl border border-teal-500/20">
                  <div className="text-lg sm:text-2xl font-black text-white mb-1">10.000+</div>
                  <div className="text-[10px] sm:text-xs text-gray-400">Mutlu Kullanıcı</div>
                </div>
                <div className="text-center p-3 sm:p-4 bg-gray-900/30 rounded-xl border border-teal-500/20">
                  <div className="flex justify-center mb-1">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="w-3 h-3 sm:w-4 sm:h-4 text-yellow-400 fill-yellow-400" />
                    ))}
                  </div>
                  <div className="text-[10px] sm:text-xs text-gray-400">5/5 Puan</div>
                </div>
                <div className="text-center p-3 sm:p-4 bg-gray-900/30 rounded-xl border border-teal-500/20">
                  <div className="text-lg sm:text-2xl font-black text-white mb-1">24 Saat</div>
                  <div className="text-[10px] sm:text-xs text-gray-400">Hızlı Kargo</div>
                </div>
              </div>

              {/* CTA Buttons */}
              <div className="flex gap-3 sm:gap-4 justify-center">
                <Button
                  onClick={() => window.open(product.product_url, '_blank')}
                  className="flex-1 sm:flex-initial bg-gradient-to-r from-teal-500 via-cyan-500 to-sky-500 hover:from-teal-400 hover:via-cyan-400 hover:to-sky-400 text-black rounded-2xl px-6 sm:px-10 py-5 sm:py-7 font-black text-base sm:text-xl shadow-lg neon-glow smooth-transition shimmer"
                  data-testid="buy-now-btn"
                >
                  <ShoppingCart className="w-5 h-5 sm:w-6 sm:h-6 mr-2 sm:mr-3" />
                  Hemen Satın Al
                  <ArrowRight className="w-5 h-5 sm:w-6 sm:h-6 ml-2 sm:ml-3" />
                </Button>
              </div>

              <p className="text-center text-xs text-gray-500 mt-6">
                ✓ Güvenli ödeme · ✓ Ücretsiz kargo · ✓ 14 gün iade garantisi
              </p>
            </div>
          )}

          {/* Back to home */}
          <div className="text-center">
            <Button
              onClick={() => navigate('/')}
              variant="outline"
              className="rounded-xl bg-gray-900/50 border-2 border-teal-500/30 text-teal-400 hover:border-teal-500 hover:bg-teal-500/10 font-bold px-8 py-4"
              data-testid="back-home-btn"
            >
              <Home className="w-5 h-5 mr-2" />
              Ana Sayfaya Dön
            </Button>
          </div>
        </main>
      </div>
    </div>
  );
};

export default ThankYou;